import { APIComponentAttributes } from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { isTruthy } from '@ardoq/common-helpers';
import {
  getCompletenessGaps,
  getCompletenessScore,
  isCompleteScore,
  type CompletenessFieldKey,
  type CompletenessGaps,
  type FreshnessGap,
  type OwnershipGap,
} from './applicationCompletenessScore';

const getFieldLabel = (
  key: CompletenessFieldKey,
  i18nLocale: I18nLocale
): string => {
  switch (key) {
    case 'functional_fit':
      return t(
        i18nLocale,
        'inventory:cellRenderers.completenessActions.prompt.field.functional_fit',
        'functional fit'
      );
    case 'features_and_usability':
      return t(
        i18nLocale,
        'inventory:cellRenderers.completenessActions.prompt.field.features_and_usability',
        'features and usability'
      );
    case 'business_strategic_fit':
      return t(
        i18nLocale,
        'inventory:cellRenderers.completenessActions.prompt.field.business_strategic_fit',
        'business strategic fit'
      );
    case 'criticality':
      return t(
        i18nLocale,
        'inventory:cellRenderers.completenessActions.prompt.field.criticality',
        'criticality'
      );
    case 'technical_integrity':
      return t(
        i18nLocale,
        'inventory:cellRenderers.completenessActions.prompt.field.technical_integrity',
        'technical integrity'
      );
    case 'maintainability_and_agility':
      return t(
        i18nLocale,
        'inventory:cellRenderers.completenessActions.prompt.field.maintainability_and_agility',
        'maintainability and agility'
      );
    case 'technical_strategic_fit':
      return t(
        i18nLocale,
        'inventory:cellRenderers.completenessActions.prompt.field.technical_strategic_fit',
        'technical strategic fit'
      );
  }
};

const labelFields = (
  keys: CompletenessFieldKey[],
  i18nLocale: I18nLocale,
  languageCode: string
): string =>
  new Intl.ListFormat(languageCode, {
    style: 'long',
    type: 'conjunction',
  }).format(keys.map(key => getFieldLabel(key, i18nLocale)));

const MAX_PROMPT_FIELD_LENGTH = 200;

const sanitizeForPrompt = (value: string): string => {
  const stripped = value.replace(/[\p{C}"]/gu, ' ').trim();
  if (stripped.length <= MAX_PROMPT_FIELD_LENGTH) return stripped;
  return `${stripped.slice(0, MAX_PROMPT_FIELD_LENGTH)}…`;
};

const buildOwnershipLine = (
  ownershipGap: OwnershipGap | null,
  i18nLocale: I18nLocale
): string | null => {
  if (ownershipGap === null) return null;
  if (ownershipGap.kind === 'missing') {
    return t(
      i18nLocale,
      'inventory:cellRenderers.completenessActions.prompt.ownershipMissing',
      '- Ownership: no owner assigned'
    );
  }
  return t(
    i18nLocale,
    'inventory:cellRenderers.completenessActions.prompt.ownershipPartial',
    { state: sanitizeForPrompt(ownershipGap.state) },
    '- Ownership: state is "{{state}}" — must reach "Managed"'
  );
};

const buildBusinessValueLine = (
  gaps: CompletenessGaps,
  i18nLocale: I18nLocale,
  languageCode: string
): string | null =>
  gaps.businessValueMissingFields.length === 0
    ? null
    : t(
        i18nLocale,
        'inventory:cellRenderers.completenessActions.prompt.businessValueGap',
        {
          fields: labelFields(
            gaps.businessValueMissingFields,
            i18nLocale,
            languageCode
          ),
        },
        '- Business value: {{fields}}'
      );

const buildTechFitLine = (
  gaps: CompletenessGaps,
  i18nLocale: I18nLocale,
  languageCode: string
): string | null =>
  gaps.techFitMissingFields.length === 0
    ? null
    : t(
        i18nLocale,
        'inventory:cellRenderers.completenessActions.prompt.techFitGap',
        {
          fields: labelFields(
            gaps.techFitMissingFields,
            i18nLocale,
            languageCode
          ),
        },
        '- Technical fit: {{fields}}'
      );

const buildFreshnessLine = (
  freshnessGap: FreshnessGap | null,
  i18nLocale: I18nLocale
): string | null => {
  if (freshnessGap === null) return null;
  if (freshnessGap.kind === 'unknown') {
    return t(
      i18nLocale,
      'inventory:cellRenderers.completenessActions.prompt.freshnessUnknown',
      '- Freshness: no update date on record'
    );
  }
  return t(
    i18nLocale,
    'inventory:cellRenderers.completenessActions.prompt.freshnessStale',
    { days: freshnessGap.days },
    '- Freshness: last updated {{days}} days ago'
  );
};

const readName = (
  component: APIComponentAttributes,
  i18nLocale: I18nLocale
): string =>
  sanitizeForPrompt(component.name || '') ||
  t(
    i18nLocale,
    'inventory:cellRenderers.completenessActions.prompt.unnamedApplication',
    'unnamed application'
  );

export const buildCompletenessPrompt = (
  component: APIComponentAttributes,
  now: Date,
  i18nLocale: I18nLocale,
  languageCode: string
): string => {
  const { score } = getCompletenessScore(component, now);
  const name = readName(component, i18nLocale);

  if (isCompleteScore(score)) {
    const completeIntro = t(
      i18nLocale,
      'inventory:cellRenderers.completenessActions.prompt.completeIntro',
      { name },
      'The application "{{name}}" now has the complete and fresh data needed for a TIME analysis.'
    );
    const timeAnalysisInstruction = t(
      i18nLocale,
      'inventory:cellRenderers.completenessActions.prompt.timeAnalysisInstruction',
      'Tell me which TIME quadrant it falls into and why. If it sits close to a quadrant boundary, say so and tell me what would move it to another one. Then recommend clear action points based on where it sits in the TIME model, what I should verify, and who I need to speak to before I act.'
    );
    const formatNote = t(
      i18nLocale,
      'inventory:cellRenderers.completenessActions.prompt.timeAnalysisFormatNote',
      'Keep it brief, use short paragraphs and bullet points.'
    );
    return [completeIntro, '', timeAnalysisInstruction, '', formatNote].join(
      '\n'
    );
  }

  const gaps = getCompletenessGaps(component, now);
  const gapLines = [
    buildOwnershipLine(gaps.ownershipGap, i18nLocale),
    buildBusinessValueLine(gaps, i18nLocale, languageCode),
    buildTechFitLine(gaps, i18nLocale, languageCode),
    buildFreshnessLine(gaps.freshnessGap, i18nLocale),
  ].filter(isTruthy);

  const intro = t(
    i18nLocale,
    'inventory:cellRenderers.completenessActions.prompt.intro',
    { name },
    'The application "{{name}}" has incomplete data in our inventory.'
  );
  const gapHeader = t(
    i18nLocale,
    'inventory:cellRenderers.completenessActions.prompt.gapHeader',
    'Missing information:'
  );
  const instruction = t(
    i18nLocale,
    'inventory:cellRenderers.completenessActions.prompt.instruction',
    'Help me close these gaps. For each item, suggest who I should talk to, what questions to ask them, and how to assess or record the value in Ardoq.'
  );
  return [intro, '', gapHeader, ...gapLines, '', instruction].join('\n');
};
